/* eslint-disable no-multi-spaces */
/* eslint key-spacing: ["error", { align: "value" }] */

import JsSIP from 'jssip'
import Message from './message'
import screenpop from './screenpop'
import stunservers from './stunservers'
import VMail from 'Store/class/vmail'
import events from 'Store/events'
import debug from 'debug'

const dbg = debug('SIPUA:JsSIP')

// SIP UA connection status
const DISCONNECTED  = 0
const CONNECTED     = 1 << 0
const REGISTERED    = 1 << 1

const connect           = Symbol('connect to proxy')
// connection state callbacks
const ON_CONNECTING     = Symbol('callback on UA connecting')
const ON_CONNECTED      = Symbol('callback on UA connected')
const ON_DISCONNECTED   = Symbol('callback on UA disconnected')
const ON_REGISTER       = Symbol('callback on UA register')
const ON_UNRGISTER      = Symbol('callback on UA unregister')
const ON_REGFAIL        = Symbol('callback on UA registration fails')
const ON_SIPEVENT       = Symbol('callback on out of dialog NOTIFY')
const ON_CALL_RECVING   = Symbol('callback on new UA new incoming call')
// call state callbacks
const CHECK_CONFIG      = Symbol('private method to validate config')

const NEW_RTC_SESSION   = Symbol('internal RTCSession wrapper')
const SESSIONS          = Symbol('JsSIP RTCSession calls array')

/**
 * SIP UA class.
 * @param {Object} Vuex store reference
 * @param {Boolean} Debug JsSIP enable/disable
 */
class SIPUA {
  constructor () {
    this.store   = null
    this.status  = DISCONNECTED
    this.config  = null
    this.ua      = null
    this.realmId = null
    this[SESSIONS] = []
  }

  /**
   * @public Gets configuration from API and init local config.
   */
  init (store, data) {
    this.store = store
    this.config = data
    this[CHECK_CONFIG]()
    this.config.display_name = data.clidName
    this[connect]()
  }

  /**
   * @public
   * Set realm id required for presence messages
   *
   * @param {Number} realmId
   */
  setRealmID (realmId) {
    this.realmId = realmId
  }

  /**
   * @public
   * Unregister SIP UA
   */
  async unregister () {
    dbg('Unregister UA contact')
    if (this.ua.isConnected() && this.ua.isRegistered()) {
      await Promise.race([
        new Promise(resolve => {
          this.ua.once('unregistered', () => {
            dbg('UA unregistered')
            resolve('unregistered')
          })
          this.ua.unregister({ all: false })
        }),
        new Promise(resolve => {
          setTimeout(() => {
            dbg('UA unregistration timed out')
            resolve('timeout')
          }, 6000)
        })
      ])
    } else {
      dbg('UA is not registered')
      return 'not-registered'
    }
  }

  async disconnect () {
    if (this.ua && this.ua.isConnected()) {
      await Promise.race([
        new Promise(resolve => {
          this.ua.once('disconnected', () => {
            dbg('UA disconnected')
            resolve('disconnected')
          })
          try {
            this.ua.stop()
          } catch (e) {
            dbg('Error calling ua.stop(): %o', e)
            resolve('error')
          }
        }),
        new Promise(resolve => {
          setTimeout(() => {
            dbg('UA disconnection timed out')
            resolve('timeout')
          }, 6000)
        })
      ])
    } else {
      return 'not-connected'
    }
  }

  /**
   * @public
   * Make call to destination with JsSIP.
   *
   * @param {String} Destination number.
   * @param {Object} Additional options
   */
  call (number, opts) {
    if (!SIPUA.isValidNum(number)) {
      events.pubError(`Invalid number: ${number}`)
      return false
    }
    const dest = this.toDestNum(number)
    const uri = new JsSIP.URI(this.scheme, dest, this.realm)
    const options = {
      mediaConstraints: { audio: true, video: false },
      eventHandlers:    {
        sending: (e) => this.store.dispatch('sipua/callSending', { event: e }),
        ended:   (e) => this.store.commit('sipua/CALL_ENDED', e)
      }
    }
    try {
      this.ua.call(uri, Object.assign(options, opts))
    } catch (e) {
      dbg('Failed to make call: %o', e)
      events.pubError('Failed to initiate call. See debug for details')
      return false
    }
    return true
  }

  /**
   * @public
   * Hangup call.
   *
   * @param {String} Call-ID to hangup
   * @return {Boolean}
   */
  callHangup (callid) {
    dbg(`callHangup callid: '${callid}'`)
    const sess = this[SESSIONS].find(s => s.callid === callid)
    if (!sess) {
      dbg(`Failed callHangup: no session with call-id "${callid}"`)
      return false
    }
    try {
      sess.srcStream.getAudioTracks().forEach(track => {
        track.stop()
      })
    } catch (err) {
      dbg('Failed to terminate source session: %o', err)
    }
    this.sessTerminate(sess.session)
    this.sessDelete(callid)
    return true
  }

  /**
   * @public
   * Accept incoming ringing call
   *
   * @param {String} Call-ID to hangup
   * @return {Boolean}
   */
  callAccept (callid) {
    const sess = this[SESSIONS].find(s => s.callid === callid)
    if (!sess) {
      dbg(`Failed callAccept: no session with call-id "${callid}"`)
      return false
    }
    if (sess.session.isEnded()) {
      dbg(`Session with callid: "${callid}" is already terminated. Do not answer.`)
      return false
    }
    try {
      sess.session.answer({
        mediaConstraints: { audio: true, video: false }
      })
      screenpop.handleRequest(screenpop.TRIGGER.ANSWERED, sess.session._request)
    } catch (err) {
      dbg('Failed answer call: %o', err)
      return false
    }
    return true
  }

  /**
   * @public
   * Reconnect the SIP UA on connection failure
   */
  handleKeepaliveFail () {
    dbg('Keepalive failed.')
    this.stopKeepAlive()

    dbg('Stopping UA...')
    try {
      this.ua.stop()
    } catch (err) {
      dbg('Error stopping UA: %o', err)
    }

    dbg('Reconnecting...')
    this[connect]()
  }

  /**
   * @public
   * Prepare number to dial
   */
  toDestNum (number) {
    return String(number).replace(/[^0-9*#+]/g, '')
  }

  /**
   * @public
   * Publish XML PIDF presence for user DND status
   * @param {Boolean} DND status on/off
   * @return void
   */
  publishDND (enabled, code) {
    if (!this.ua) {
      dbg('SIP UA is not configured. Ignore SIP DND BLF publish')
      return
    }
    const msg = new Message('PUBLISH', this.ua)
    msg.publishPresence(!enabled)

    const presence = msg.toString()
    const dialog = this.publishDialogMsg(enabled, code)

    try {
      this.ua.transport.socket.send(presence)
      this.ua.transport.socket.send(dialog)
    } catch (err) {
      events.pubError(`Failed to send PUBLISH DND: ${err.message}`)
    }
  }

  /**
   * @public
   * Publish XML PIDF presence for user DND status
   * @param {Boolean} DND status on/off
   * @return void
   */
  publishCallFwd (enabled, code) {
    if (!this.ua) {
      dbg('SIP UA is not configured. Ignore SIP Forward BLF publish')
      return
    }
    const packet = this.publishDialogMsg(enabled, code)

    try {
      this.ua.transport.socket.send(packet)
    } catch (err) {
      events.pubError(`Failed to send PUBLISH Call Forward status: ${err.message}`)
    }
  }

  /**
   * @public
   * Publish XML PIDF presence for user Follow-me status
   * @param {Boolean} DND status on/off
   * @return void
   */
  publishFollowme (enabled, code) {
    if (!this.ua) {
      dbg('SIP UA is not configured. Ignore SIP FollowMe BLF publish')
      return
    }
    const packet = this.publishDialogMsg(enabled, code)

    try {
      this.ua.transport.socket.send(packet)
    } catch (err) {
      events.pubError(`Failed to send PUBLISH Follow-me status: ${err.message}`)
    }
  }

  publishDialogMsg (enabled, code) {
    const msg = new Message('PUBLISH', this.ua)
    msg.uri = new JsSIP.URI('sip', `${code}${msg.uri.user}`, msg.uri.host)
    const identity = msg.uri.toString()
    msg.publishDialog(enabled, msg.uri.user, identity, this.realmId)

    const packet = msg.toString()
    dbg(packet)

    return packet
  }

  /**
   * @public
   * Get Media from sessions array by session id
   * @param {String} Session Id
   * @return RTCSession or null
   * @throws Exception
   */
  getMedia (callid, devIn) {
    const sess = this[SESSIONS].find(s => s.callid === callid)

    if (!sess) {
      return null
    }
    // set microphone
    navigator.mediaDevices.getUserMedia({ audio: { deviceId: devIn }, video: false })
      .then(stream => {
        sess.srcStream = stream
        const senders = sess.session.connection.getSenders()
        senders.forEach(conn => {
          const track = stream.getAudioTracks()
          conn.replaceTrack(track[0])
        })
      }).catch(err => {
        dbg('Failed to setup micro media device: %o', err)
      })

    const stream = new MediaStream()
    try {
      const recv = sess.session.connection.getReceivers()
      recv.forEach(function (conn) {
        stream.addTrack(conn.track)
      })
    } catch (err) {
      dbg('Failed to setup receivers media tracks: %o', err)
    }
    return stream
  }

  /**
   * @public
   * Get RTP Session for call by call-id
   *
   * @param {String} CallId
   * @return {Object} RTCSession
   */
  getRTPSession (callid) {
    const sess = this[SESSIONS].find(s => s.callid === callid)

    if (!sess) {
      return null
    }
    return sess.session
  }

  /**
   * @public
   * Sends OPTIONS message
   *
   * @param {String} user
   * @return {Boolean}
   */
  sendOptions (user) {
    try {
      const targetUser = user || this.username
      const targetUri = new JsSIP.URI(this.scheme, targetUser, this.realm)
      this.ua.sendOptions(targetUri, null, {
        eventHandlers: {
          succeeded: () => {
            this._optionsFailures = 0
            dbg('OPTIONS succeeded')
          },
          failed: e => {
            this._optionsFailures = (this._optionsFailures || 0) + 1
            dbg(`Failed to send ${this._optionsFailures} OPTIONS message(s): %o`, e)
            if (this._optionsFailures >= 2) {
              this.handleKeepaliveFail()
            }
          }
        }
      })
    } catch (e) {
      dbg('Failed to send OPTIONS: %o', e)
      return false
    }
    return true
  }

  /**
   * @public
   * Check if session is ready to be terminated and terminates the
   * session. Also, handles exceptions.
   * @param {RTCSession}
   * @return void
   */
  sessTerminate (sess) {
    dbg('sessTerminate: start')
    if (!sess) {
      dbg('sessTerminate: undefined or null session')
      return
    }

    if (sess.isEnded()) {
      dbg('sessTerminate: session is already terminated')
      return
    }

    try {
      sess.terminate()
    } catch (error) {
      dbg('sessTerminate: failed: %o', error)
    }
  }

  /**
   * @public
   * Get Media from sessions array by session id
   * @param {String} Session Id
   * @return RTCSession or null
   */
  sessDelete (callid) {
    const idx = this[SESSIONS].findIndex(s => s.callid === callid)
    dbg(`sessDelete session with call-id "${callid}"`)
    if (idx === -1) {
      dbg('Failed to find session in stack')
      return
    }
    this[SESSIONS].splice(idx, 1)
  }

  /**
   * @public
   * Set session hold/unhold
   *
   * @param {String} Call-ID
   * @param {Boolean}
   */
  setOnHold (callid, enable) {
    try {
      const sess = this[SESSIONS].find(s => s.callid === callid)
      if (!sess) {
        dbg(`Failed to set hold/unhold. Session not found: ${callid}`)
        return
      }
      for (let i = 0; i < this[SESSIONS].length; i++) {
        if (this[SESSIONS][i].callid !== callid) {
          this[SESSIONS][i].session.hold()
        }
      }

      if (enable) {
        sess.session.hold()
      } else {
        sess.session.unhold()
      }
    } catch (err) {
      dbg('Failed to set hold/unhold: %o', err)
      events.pubError(`Failed to put call on hold: ${err.message}`)
    }
  }

  /**
   * @public
   * Set session mute/unmute
   *
   * @param {String} Call-ID
   * @param {Boolean}
   */
  setMute (callid, enable) {
    try {
      const sess = this[SESSIONS].find(s => s.callid === callid)
      if (!sess) {
        dbg(`Failed to set mute/unmute. Session not found: ${callid}`)
        return
      }

      if (enable) {
        sess.session.mute()
      } else {
        sess.session.unmute()
      }
    } catch (err) {
      dbg('Failed to set mute/unmute: %o', err)
      events.pubError(`Failed to mute/unmute call: ${err.message}`)
    }
  }

  /**
   * @public
   * Send DTMF signal to RTP session.
   *
   * @param {String} call-id
   * @param {Char} DTMF char
   */
  sendDTMF (callid, key) {
    const sess = this[SESSIONS].find(s => s.callid === callid)
    if (!sess) {
      dbg(`Failed to send DTMF ${key}. Session not found: ${callid}`)
      return
    }
    try {
      sess.session.sendDTMF(key, { transportType: JsSIP.C.DTMF_TRANSPORT.INFO })
    } catch (err) {
      dbg('Failed to send DTMF: %o', err)
      events.pubError(`Failed to send DTMF: ${err.message}`)
    }
  }

  /**
   * @public
   * Sends a keepalive OPTIONS message every 90s
   * @return void
   */
  startKeepAlive () {
    if (this.optionsKeepalive) {
      clearInterval(this.optionsKeepalive)
    }
    this._optionsFailures = 0
    this.optionsKeepalive = setInterval(() => {
      if (this.registered) {
        this.sendOptions()
      }
    }
    , 90000)
  }

  /**
   * @public
   * Stops sending keepalive OPTIONS messages
   * @return void
   */
  stopKeepAlive () {
    if (this.optionsKeepalive) {
      clearInterval(this.optionsKeepalive)
      this.optionsKeepalive = null
      this._optionsFailures = 0
    }
  }

  /**
   * @public
   * Transfer call
   *
   * @param {String} callid Call-ID of the active call to x-fer
   * @param {String} num Number to transfer
   * @param {String} replaceCallid Call-ID to replace for attended transfer
   * @param {Object} opts Additional options
   */
  callXFer (callid, num, replaceCallid, opts) {
    dbg(`Attended x-fer: callid: ${callid}, number: ${num}, replace: ${replaceCallid}`)
    const sess = this[SESSIONS].find(s => s.callid === callid)
    if (!sess) {
      dbg(`Failed to x-fer call to ${num}. Session not found: ${callid}`)
      events.pubError({
        key:  'sipua.xfer.failed',
        args: { num: num, fail: 'Referer session not found' }
      })
      return
    }
    let options = {
      eventHandlers: {
        requestSucceeded: () => {
          events.pubNotify({ text: 'sipua.xfer.init', args: { num: num } })
        },
        requestFailed: e => {
          events.pubError({ key: 'sipua.xfer.failed', args: { num: num, fail: e.cause } })
        },
        accepted: e => {
          this.store.commit('sipua/XFER_ACCEPTED_NOTIFY', num)
          // make sure to hangup both sessions
          this.store.commit('sipua/SESSION_DELETE', replaceCallid)
          this.store.commit('sipua/SESSION_DELETE', callid)
        },
        failed: e => {
          events.pubError({
            key:  'sipua.xfer.failed',
            args: { num: num, fail: e.status_line?.reason_phrase }
          })
        }
      }
    }

    if (replaceCallid) {
      const replace = this[SESSIONS].find(s => s.callid === replaceCallid)
      if (!replaceCallid) {
        events.pubError({
          key:  'sipua.xfer.failed',
          args: { num: num, fail: 'Refer replace session not found' }
        })
        dbg(`Failed to confirm attendd x-fer call to ${num}. Session to replace is not found: ${replaceCallid}`)
        return
      }
      options.replaces = replace.session
    }

    if (opts) {
      options = { ...options, ...opts }
    }

    try {
      sess.session.refer(num, options)
    } catch (err) {
      dbg('Failed to refer: %o', err)
      events.pubError(`Failed to refer call: ${err.message}`)
    }
  }

  /**
   * @public
   * Enable debugging.
   *
   * @param {Boolean}
   */
  debug (enable) {
    if (enable) {
      JsSIP.debug.enable('*')
    } else {
      JsSIP.debug.disable('*')
    }
  }

  get sessions () { return this[SESSIONS] }
  get proxy  () { return this.config?.proxy }
  get uri    () { return this.config?.uri || '' }
  get realm  () { return this.config?.realm }
  get ha1    () { return this.config?.ha1 }
  get scheme () {
    const uri = JsSIP.URI.parse(this.uri)
    return uri ? uri.scheme : 'sip'
  }

  get username () {
    const uri = JsSIP.URI.parse(this.uri)
    return uri ? uri.user : ''
  }

  get registered () {
    return (this.status & CONNECTED) === CONNECTED &&
      (this.status & REGISTERED) === REGISTERED
  }

  get userAgent () {
    const ver = this.config.uaVer || 'UNKNOWN'
    return `Call Panel ${ver}`
  }

  /**
   * @static
   * Test if given number is valid to call
   */
  static isValidNum (number) {
    if (!number) {
      return false
    }
    const num = String(number).trim()

    if (num.length === 0) {
      return false
    }

    return /^(\+|\*{1,3})?\(?(?=[0-9*#])[0-9 )*#\-.]+$/.test(num)
  }

  static isConnected (status) {
    return (status & CONNECTED) === CONNECTED
  }

  static isRegistered (status) {
    return (status & CONNECTED) === CONNECTED &&
      (status & REGISTERED) === REGISTERED
  }

  static isWebRTCSupported () {
    return !!window.RTCPeerConnection
  }

  /**
   * @private Connect to the WSS SIP proxy.
   */
  async [ connect ] () {
    try {
      if (!SIPUA.isWebRTCSupported()) {
        throw new Error('WebRTC is not supported. Try to upgrade your browser')
      }

      await this.destroy()

      const socks = []
      // set connection proxies
      for (const proxy of this.proxy) {
        socks.push({
          socket: new JsSIP.WebSocketInterface(proxy.url),
          weight: proxy.weight
        })
      }

      // stun servers configuration
      let stunServersArray = []
      const stunServersString = stunservers.get()
      if (stunServersString) {
        stunServersArray = stunServersString.split(',').map(s => s.trim()).filter(s => s)
        dbg('Using STUN servers: %s', stunServersArray.join(', '))
      }
      const stunServersConfig = stunServersArray.length > 0 ? { stun_servers: stunServersArray } : {}

      const ua = new JsSIP.UA({
        ...this.config,
        ...stunServersConfig,
        // JsSIP can use INVITE to update session. Force sessions timers
        session_timers: false,
        // session_timers_refresh_method: 'invite',
        user_agent:     this.userAgent,
        sockets:        socks
      })
      ua.on('connecting',         () => this[ON_CONNECTING]())
      ua.on('connected',          () => this[ON_CONNECTED]())
      ua.on('disconnected',       () => this[ON_DISCONNECTED]())
      ua.on('registered',         () => this[ON_REGISTER]())
      ua.on('unregistered',       () => this[ON_UNRGISTER]())
      ua.on('registrationFailed', (e) => this[ON_REGFAIL](e))
      ua.on('newRTCSession',      (e) => this[NEW_RTC_SESSION](e))
      ua.on('sipEvent',           (e) => this[ON_SIPEVENT](e))
      ua.start()
      this.ua = ua

      this.startKeepAlive() // send keepalive OPTIONS
    } catch (e) {
      events.pubError(`SIP UA: ${e.message}`)
    }
  }

  /**
   * @private
   * Validate configuration received from server.
   * @throw
   */
  [ CHECK_CONFIG ] () {
    const cnf = this.config
    if (!cnf) throw new Error('Missing configuration from server.')
    if (!cnf.ha1) throw new Error('Configuration hash HA1 is not defined.')
    if (cnf.ha1 && (cnf.ha1.trim().length !== 32)) {
      throw new Error('Invalid HA1 hash.')
    }
    if (!cnf.proxy) throw new Error('Missing SIP WebSocket proxy address.')
    if (!Array.isArray(cnf.proxy)) throw new Error('WSS Proxy invalid value. Must be array of objects.')
    for (let i = 0; i < cnf.proxy.length; i++) {
      if (!cnf.proxy[i].url) throw new Error(`WSS Proxy invalid URL value for proxy #${i}`)
    }
    if (!cnf.realm) throw new Error('Missing SIP realm.')
    if (!cnf.uri) throw new Error('Missing SIP URI.')
  }

  [ ON_CALL_RECVING ] (event) {
    const req = event.request
    if (!req) {
      dbg('ERROR: Invalid event in CALL_RECV mutation: %o', event)
      return
    }
    const data = {
      callid:   req.call_id,
      name:     req.from.display_name,
      number:   req.from.uri.user,
      autoAnsw: /answer-after=0$/.test(req.getHeader('Call-Info')),
      recvCall: true,
      outgoing: false
    }

    this.store.dispatch('sipua/callInit', data)
    screenpop.handleRequest(screenpop.TRIGGER.RINGING, req)
  }

  [ ON_CONNECTING ] () {
    dbg('SIP UA start connecting.')
  }

  [ ON_CONNECTED ] () {
    this.store.dispatch('sipua/setStatus', this.status |= CONNECTED)
  }

  [ ON_DISCONNECTED ] () {
    this.store.dispatch('sipua/setStatus', this.status ^= CONNECTED)
  }

  [ ON_REGISTER ] () {
    this.store.dispatch('sipua/setStatus', this.status |= REGISTERED)
  }

  [ ON_UNRGISTER ] () {
    this.store.dispatch('sipua/setStatus', this.status ^= REGISTERED)
  }

  [ ON_REGFAIL ] (e) {
    events.pubError(`Web Phone Register error: ${e.cause} - ${e.response?.reason_phrase}`)
    if ((this.status & REGISTERED) === REGISTERED) {
      this.store.dispatch('sipua/setStatus', this.status ^= REGISTERED)
    }
  }

  [ NEW_RTC_SESSION ] (event) {
    // calls limit
    if (this.store.getters['sipua/maxCallsHit']) {
      events.pubError({
        key:  'sipua.error.maxCallsIn',
        args: {
          limit:    this.store.getters['sipua/maxCalls'],
          fromName: event.request.from.display_name,
          fromNum:  event.request.from.uri.user
        }
      })
      if (event.session) {
        this.sessTerminate(event.session)
      }
      return
    }

    const session = {
      originator: event.originator,
      callid:     event.request.call_id,
      session:    event.session // : RTCSession
    }
    this[SESSIONS].push(session)
    dbg(`New session with call-id "${session.callid}"`)

    event.session.on('accepted', () => this.store.commit('sipua/NEW_RTC_SESSION', event))
    event.session.on('failed', e => this.store.commit('sipua/CALL_FAILED', e))
    event.session.on('reinvite', e => this.store.commit('sipua/ON_REINVITE', e))
    event.session.on('ended', e => this.store.commit('sipua/CALL_ENDED', e))
    event.session.on('icecandidate', e => this.store.commit('sipua/ON_ICECANDIDATE', e))
    if (event.originator === 'remote') {
      this[ON_CALL_RECVING](event)
    }
  }

  /**
   * @private SIP UA calls callbacks
   */
  [ ON_SIPEVENT ] (e) {
    if (e.event.event !== 'message-summary') {
      // treat only MWI events
      return
    }
    const vm = VMail.parseMWIBody(e.request.body)
    if (vm) {
      events.pubReloadVoicemails(vm.new)
    }
  }

  async destroy () {
    if (!this.ua) {
      return true
    }

    this.stopKeepAlive()
    await this.disconnect()

    this.ua = null
    this.status = DISCONNECTED
    return true
  }

  // called by unload, cant have promise in there
  // vuex store wrap getter and actions in a promise
  destroyOnUnload () {
    try {
      if (this.ua) {
        this.stopKeepAlive()

        try {
          this.ua.stop()
        } catch (err) {
          dbg('UA stop failed on unload: %o', err)
        }

        this.ua = null
        this.status = DISCONNECTED
      }
    } catch (e) {
      dbg('SIPUA destroyOnUnload error: %o', e)
    }
  }
}

export default SIPUA
